/**
 * Fetch wrapper for the Momentum Concierge worker API.
 *
 * - Base URL from VITE_API_BASE (default: same origin for local dev through
 *   a proxy; in production, points at the worker — e.g. chat.momentum-wp.com).
 * - Attaches Authorization: Bearer <token> when a session token is stored.
 * - On 401, clears the session so routed components flip to /login.
 */

import { clearSession, getToken } from '../auth/session'

const API_BASE: string = (import.meta.env.VITE_API_BASE as string) ?? ''

export class ApiError extends Error {
  status: number
  data: unknown
  constructor(status: number, message: string, data: unknown) {
    super(message)
    this.status = status
    this.data = data
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  body?: unknown
  headers?: Record<string, string>
  auth?: boolean // default true; set false for /auth/request and /auth/verify
  signal?: AbortSignal
}

export async function api<T = unknown>(
  path: string,
  opts: RequestOptions = {}
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...opts.headers,
  }

  if (opts.auth !== false) {
    const token = getToken()
    if (token) headers['Authorization'] = `Bearer ${token}`
  }

  const url = API_BASE + path
  const response = await fetch(url, {
    method: opts.method ?? (opts.body ? 'POST' : 'GET'),
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  })

  let data: unknown
  const text = await response.text()
  if (text.length > 0) {
    try {
      data = JSON.parse(text)
    } catch {
      data = text
    }
  }

  if (!response.ok) {
    if (response.status === 401 && opts.auth !== false) {
      clearSession()
    }
    const body = data && typeof data === 'object' ? (data as { error?: string; message?: string }) : null
    const message = body?.message || body?.error || `HTTP ${response.status}` || 'Request failed'
    throw new ApiError(response.status, message, data)
  }

  return data as T
}
